import { Star, Plus, X } from "lucide-react";

import { MODULOS } from "./ModuleCards";

/**
 * As perguntas da avaliação. Cada uma vira uma linha de 5 estrelas para o
 * cliente e escolhe em quais módulos aparece (dono, 26/10/2026): "Como foi a
 * entrega?" não aparece para quem comeu na mesa. Sem módulo marcado = todos.
 * O `key` é interno (gerado pela janela) — o lojista só edita o texto.
 */
export function DimensionsList({ dimensions = [], ativos = [], onUpdate, onAdd, onRemove }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Star className="w-4 h-4 text-amber-600 fill-amber-600" />
          <h3 className="text-sm font-bold">Perguntas</h3>
          <span className="text-[11px] text-muted-foreground tabular-nums">({dimensions.length}/5)</span>
        </div>
        <button
          type="button"
          onClick={onAdd}
          disabled={dimensions.length >= 5}
          data-e2e="adicionar-pergunta"
          className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-3.5 h-3.5" /> Adicionar
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2 leading-snug">
        Cada pergunta vira uma linha de 5 estrelas para o cliente. Marque em quais pedidos ela aparece.
      </p>
      <div className="space-y-2">
        {dimensions.map((d, i) => (
          <Pergunta
            key={d.key}
            index={i}
            pergunta={d}
            ativos={ativos}
            onChange={(patch) => onUpdate(i, patch)}
            onRemove={() => onRemove(i)}
            canRemove={dimensions.length > 1}
          />
        ))}
      </div>
    </section>
  );
}

function Pergunta({ index, pergunta, ativos, onChange, onRemove, canRemove }) {
  const marcados = pergunta.modules || [];
  const todos = marcados.length === 0;
  // Só os módulos ligados na campanha viram opção; um marcado que foi
  // desligado continua aparecendo para poder ser desmarcado
  const opcoes = MODULOS.filter((m) => ativos.includes(m.id) || marcados.includes(m.id));
  const alternar = (id) => {
    const novo = marcados.includes(id) ? marcados.filter((x) => x !== id) : [...marcados, id];
    onChange({ modules: MODULOS.map((m) => m.id).filter((x) => novo.includes(x)) });
  };
  const chip = (ativo) =>
    `rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
      ativo ? "border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-300" : "border-border text-muted-foreground hover:bg-muted"
    }`;
  return (
    <div className="rounded-xl border p-2.5 space-y-2" data-e2e="pergunta">
      <div className="flex items-center gap-1.5">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/10 text-[10px] font-bold text-amber-700 dark:text-amber-300 shrink-0 tabular-nums">
          {index + 1}
        </div>
        <input
          type="text"
          value={pergunta.label || ""}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Ex: Como foi a comida?"
          maxLength={80}
          data-e2e="pergunta-texto"
          className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-background text-sm focus:border-amber-500/60 focus:outline-none focus:ring-1 focus:ring-amber-500/30"
        />
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          title={canRemove ? "Remover pergunta" : "Você precisa manter ao menos 1 pergunta"}
          className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-30 disabled:cursor-not-allowed shrink-0 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 pl-7">
        <span className="text-[11px] text-muted-foreground mr-0.5">Aparece em:</span>
        <button type="button" className={chip(todos)} aria-pressed={todos} onClick={() => onChange({ modules: [] })} data-e2e="pergunta-todos">
          Todos
        </button>
        {opcoes.map((m) => (
          <button key={m.id} type="button" className={chip(!todos && marcados.includes(m.id))} aria-pressed={marcados.includes(m.id)}
            onClick={() => alternar(m.id)} data-e2e={`pergunta-modulo-${m.id}`}>
            {m.label}
          </button>
        ))}
      </div>
    </div>
  );
}
